import React, { useEffect, useState, useRef } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import BlockEditor from '../components/BlockEditor';
import { useJsApiLoader } from '@react-google-maps/api';
import './NewsManagement.css';

export default function AttractionsManagement() {
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAttraction, setEditingAttraction] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    address: '',
    status: 'draft',
  });
  const [blocks, setBlocks] = useState([{ 
    type: 'text', 
    text: '', 
    align: 'left',
    bold: false,
    italic: false,
    underline: false
  }]);
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [existingGalleryImages, setExistingGalleryImages] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const mapDivRef = useRef(null);

  // Ładowanie Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places'],
  });

  useEffect(() => {
    loadAttractions();
  }, []);

  // Inicjalizacja mapy Google i autocomplete (tylko gdy modal jest otwarty i API załadowane)
  useEffect(() => {
    if (!showModal) return;
    if (!isLoaded) return;
    if (loadError) {
      toast.error('Błąd ładowania Google Maps API');
      return;
    }
    if (!mapDivRef.current) return;
    if (!window.google || !window.google.maps) return;

    const lat = formData.latitude ? parseFloat(formData.latitude) : 52.2297;
    const lng = formData.longitude ? parseFloat(formData.longitude) : 21.0122;

    const map = new window.google.maps.Map(mapDivRef.current, {
      center: { lat, lng },
      zoom: 14,
    });

    const marker = new window.google.maps.Marker({
      position: map.getCenter(),
      map: map,
      draggable: true,
    });

    marker.addListener('dragend', (e) => {
      setFormData(prev => ({
        ...prev,
        latitude: e.latLng.lat().toString(),
        longitude: e.latLng.lng().toString(),
      }));
    });

    if (searchInputRef.current && window.google.maps.places) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        searchInputRef.current
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) return;

        map.setCenter(place.geometry.location);
        marker.setPosition(place.geometry.location);

        setFormData(prev => ({
          ...prev,
          latitude: place.geometry.location.lat().toString(),
          longitude: place.geometry.location.lng().toString(),
          address: place.formatted_address || prev.address,
        }));
      });
    }

    const clickListener = map.addListener('click', (e) => {
      const clickedLat = e.latLng.lat();
      const clickedLng = e.latLng.lng();
      marker.setPosition({ lat: clickedLat, lng: clickedLng });
      setFormData(prev => ({
        ...prev,
        latitude: clickedLat.toString(),
        longitude: clickedLng.toString(),
      }));
    });

    return () => {
      window.google.maps.event.removeListener(clickListener);
      marker.setMap(null);
    };
  }, [showModal, isLoaded, loadError, formData.latitude, formData.longitude]);


  // Convert blocks to HTML for backward compatibility (description field)
  function blocksToHtml(blocks) {
    if (!blocks || blocks.length === 0) return '';
    
    return blocks.map(block => {
      if (block.type === 'text') {
        let processed = block.text || '';
        
        // Apply formatting based on block properties
        if (block.bold) {
          processed = `<strong>${processed}</strong>`;
        }
        if (block.italic) {
          processed = `<em>${processed}</em>`;
        }
        if (block.underline) {
          processed = `<u>${processed}</u>`;
        }
        
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        return `<p ${alignStyle}>${processed}</p>`;
      } else if (block.type === 'divider') {
        return '<hr/>';
      } else if (block.type === 'link') {
        const text = block.text || '';
        const url = block.url || '#';
        const alignStyle = `style="text-align:${block.align || 'left'}"`;
        // Only include link if URL is valid
        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
          return `<p ${alignStyle}><a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a></p>`;
        }
        return `<p ${alignStyle}>${text}</p>`;
      }
      return '';
    }).join('');
  }

  // Convert HTML/description to blocks (for backward compatibility)
  function htmlToBlocks(html) {
    if (!html) return [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }];
    
    const blocks = [];
    
    // Split by <p> tags, <hr/>, or <hr> to preserve structure
    const parts = html.split(/(<p[^>]*>.*?<\/p>|<hr\s*\/?>)/gi);
    
    for (const part of parts) {
      if (!part || !part.trim()) continue;
      
      const trimmedPart = part.trim();
      
      // Check for divider
      if (/<hr\s*\/?>/i.test(trimmedPart)) {
        blocks.push({ type: 'divider' });
        continue;
      }
      
      // Check for paragraph
      const pMatch = trimmedPart.match(/<p[^>]*>(.*?)<\/p>/i);
      if (pMatch) {
        const pContent = pMatch[1];
        const pTag = pMatch[0];
        
        // Extract alignment
        let align = 'left';
        const alignMatch = pTag.match(/text-align:\s*(left|center|right)/i);
        if (alignMatch) {
          align = alignMatch[1].toLowerCase();
        }
        
        // Check if this is a link
        const linkMatch = pContent.match(/<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/i);
        if (linkMatch) {
          blocks.push({
            type: 'link',
            text: linkMatch[2].trim(),
            url: linkMatch[1] || '',
            align: align
          });
          continue;
        }
        
        // Extract text and formatting
        let text = pContent;
        let bold = /<strong>|<b>/i.test(text);
        let italic = /<em>|<i>/i.test(text);
        let underline = /<u>/i.test(text);
        
        // Remove formatting tags
        text = text.replace(/<\/?strong>|<\/?b>/gi, '');
        text = text.replace(/<\/?em>|<\/?i>/gi, '');
        text = text.replace(/<\/?u>/gi, '');
        text = text.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
        
        if (text) {
          blocks.push({
            type: 'text',
            text: text,
            align: align,
            bold: bold,
            italic: italic,
            underline: underline
          });
        }
      } else if (!/<[^>]+>/.test(trimmedPart)) {
        // Plain text without tags
        const text = trimmedPart.replace(/&nbsp;/g, ' ').trim();
        if (text) {
          blocks.push({
            type: 'text',
            text: text,
            align: 'left',
            bold: false,
            italic: false,
            underline: false
          });
        }
      }
    }
    
    return blocks.length > 0 ? blocks : [{ 
      type: 'text', 
      text: '', 
      align: 'left',
      bold: false,
      italic: false,
      underline: false
    }];
  }

  const loadAttractions = async () => {
    try {
      // Pobierz atrakcje z galerią zdjęć (Problem 4)
      const { data, error } = await supabaseAdmin
        .from('attractions')
        .select(`
          *,
          attraction_images (*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAttractions(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania atrakcji');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMainImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Walidacja typu pliku
    if (!file.type.startsWith('image/')) {
      toast.error('Wybierz plik obrazu');
      return;
    }

    // Walidacja rozmiaru (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Obraz jest za duży. Maksymalny rozmiar: 5MB');
      return;
    }

    setMainImageFile(file);

    // Preview obrazu
    const reader = new FileReader();
    reader.onloadend = () => {
      setMainImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadMainImage = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `attraction-images/main/${fileName}`;

      const { error: uploadError } = await supabaseAdmin.storage
        .from('attraction-images')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabaseAdmin.storage
        .from('attraction-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading main image:', error);
      throw error;
    }
  };

  const handleGallerySelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Walidacja wszystkich plików
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        invalidFiles.push(file.name);
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        invalidFiles.push(file.name);
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Nieprawidłowe pliki: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length > 0) {
      setGalleryFiles([...galleryFiles, ...validFiles]);

      // Preview wszystkich plików
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setGalleryPreviews(prev => [...prev, { file, preview: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeGalleryFile = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  const uploadGalleryImage = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `attraction-images/gallery/${fileName}`;

      const { error: uploadError } = await supabaseAdmin.storage
        .from('attraction-images')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabaseAdmin.storage
        .from('attraction-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      throw error;
    }
  };

  const deleteGalleryImage = async (imageId, imageUrl) => {
    try {
      // Usuń z bazy danych
      const { error: dbError } = await supabaseAdmin
        .from('attraction_images')
        .delete()
        .eq('id', imageId);

      if (dbError) throw dbError;

      // Usuń z Storage
      const pathParts = imageUrl.split('/');
      const fileName = pathParts[pathParts.length - 1];
      const filePath = `attraction-images/gallery/${fileName}`;

      const { error: storageError } = await supabaseAdmin.storage
        .from('attraction-images')
        .remove([filePath]);

      if (storageError) {
        console.warn('Error deleting from storage:', storageError);
        // Nie przerywamy - rekord już usunięty z DB
      }

      toast.success('Zdjęcie usunięte');
      loadAttractions();
    } catch (error) {
      toast.error('Błąd podczas usuwania zdjęcia');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert blocks to HTML for backward compatibility
      const htmlFromBlocks = blocksToHtml(blocks);
      
      // Walidacja wymaganych pól
      if (!formData.name.trim() || !htmlFromBlocks.trim()) {
        toast.error('Wypełnij wszystkie wymagane pola');
        return;
      }

      if (!formData.latitude || !formData.longitude) {
        toast.error('Wprowadź współrzędne geograficzne');
        return;
      }

      const latitude = parseFloat(formData.latitude);
      const longitude = parseFloat(formData.longitude);

      if (isNaN(latitude) || isNaN(longitude)) {
        toast.error('Współrzędne geograficzne muszą być liczbami');
        return;
      }

      // Walidacja zdjęcia głównego (wymagane dla nowej atrakcji)
      if (!editingAttraction && !mainImageFile) {
        toast.error('Wybierz zdjęcie główne atrakcji');
        return;
      }

      // Upload zdjęcia głównego (jeśli wybrane)
      let mainImagePath = editingAttraction?.main_image_path || null;
      if (mainImageFile) {
        setUploadingMainImage(true);
        try {
          // Usuń stare zdjęcie główne (jeśli edycja i zmieniono zdjęcie)
          if (editingAttraction?.main_image_path) {
            const oldPath = editingAttraction.main_image_path.split('/').slice(-2).join('/');
            await supabaseAdmin.storage
              .from('attraction-images')
              .remove([oldPath]);
          }

          mainImagePath = await uploadMainImage(mainImageFile);
        } catch (uploadError) {
          toast.error('Błąd podczas przesyłania zdjęcia głównego');
          setUploadingMainImage(false);
          return;
        } finally {
          setUploadingMainImage(false);
        }
      }

      let attractionId;

      if (editingAttraction) {
        const { data, error } = await supabaseAdmin
          .from('attractions')
          .update({
            name: formData.name.trim(),
            description: htmlFromBlocks,
            blocks: blocks,
            latitude: latitude,
            longitude: longitude,
            address: formData.address.trim() || null,
            main_image_path: mainImagePath,
            status: formData.status,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingAttraction.id)
          .select()
          .single();

        if (error) {
          console.error('Supabase update error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(error.message || 'Błąd podczas aktualizacji atrakcji');
        }
        attractionId = data.id;
        toast.success('Atrakcja zaktualizowana');
      } else {
        const { data, error } = await supabaseAdmin
          .from('attractions')
          .insert({
            name: formData.name.trim(),
            description: htmlFromBlocks,
            blocks: blocks,
            latitude: latitude,
            longitude: longitude,
            address: formData.address.trim() || null,
            main_image_path: mainImagePath,
            status: formData.status,
          })
          .select()
          .single();

        if (error) {
          console.error('Supabase insert error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(error.message || 'Błąd podczas dodawania atrakcji');
        }
        attractionId = data.id;
        toast.success('Atrakcja dodana');
      }

      // Upload zdjęć galerii (jeśli wybrane)
      if (galleryFiles.length > 0) {
        setUploadingGallery(true);
        try {
          const galleryUploads = [];
          for (let i = 0; i < galleryFiles.length; i++) {
            const imageUrl = await uploadGalleryImage(galleryFiles[i]);
            galleryUploads.push({
              attraction_id: attractionId,
              image_url: imageUrl
            });
          }

          if (galleryUploads.length > 0) {
            const { error: galleryError } = await supabaseAdmin
              .from('attraction_images')
              .insert(galleryUploads);

            if (galleryError) {
              console.error('Error saving gallery images:', galleryError);
              toast.error('Atrakcja zapisana, ale wystąpił błąd podczas zapisywania galerii');
            }
          }
        } catch (galleryUploadError) {
          console.error('Error uploading gallery:', galleryUploadError);
          toast.error('Atrakcja zapisana, ale wystąpił błąd podczas przesyłania galerii');
        } finally {
          setUploadingGallery(false);
        }
      }

      setShowModal(false);
      setEditingAttraction(null);
      setFormData({
        name: '',
        description: '',
        latitude: '',
        longitude: '',
        address: '',
        status: 'draft',
      });
      setBlocks([{ 
        type: 'text', 
        text: '', 
        align: 'left',
        bold: false,
        italic: false,
        underline: false
      }]);
      setMainImageFile(null);
      setMainImagePreview(null);
      setGalleryFiles([]);
      setGalleryPreviews([]);
      setExistingGalleryImages([]);
      setSearchQuery('');
      loadAttractions();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas zapisywania');
      console.error('Error saving attraction:', error);
    }
  };

  const handleEdit = (item) => {
    setEditingAttraction(item);
    setFormData({
      name: item.name,
      description: item.description,
      latitude: item.latitude.toString(),
      longitude: item.longitude.toString(),
      address: item.address || '',
      status: item.status || 'draft',
    });
    // Load blocks from DB or convert from description for backward compatibility
    if (item.blocks && Array.isArray(item.blocks) && item.blocks.length > 0) {
      // Ensure all blocks have required properties
      const normalizedBlocks = item.blocks.map(block => {
        if (block.type === 'text') {
          return {
            ...block,
            bold: block.bold ?? false,
            italic: block.italic ?? false,
            underline: block.underline ?? false,
            align: block.align || 'left'
          };
        } else if (block.type === 'link') {
          return {
            ...block,
            text: block.text || '',
            url: block.url || '',
            align: block.align || 'left'
          };
        }
        return block;
      });
      setBlocks(normalizedBlocks);
    } else {
      // Convert old description to blocks
      const convertedBlocks = htmlToBlocks(item.description || '');
      setBlocks(convertedBlocks);
    }
    setMainImageFile(null);
    setMainImagePreview(item.main_image_path || null);
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setExistingGalleryImages(item.attraction_images || []);
    setSearchQuery('');
    setShowModal(true);
  };

  const handlePublish = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'published' ? 'draft' : 'published';
      const { error } = await supabaseAdmin
        .from('attractions')
        .update({
          status: newStatus,
        })
        .eq('id', id);

      if (error) throw error;
      toast.success(newStatus === 'published' ? 'Opublikowano' : 'Cofnięto publikację');
      loadAttractions();
    } catch (error) {
      toast.error('Błąd podczas zmiany statusu');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę atrakcję?')) return;

    try {
      const { error } = await supabaseAdmin
        .from('attractions')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Supabase delete error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw new Error(error.message || 'Błąd podczas usuwania atrakcji');
      }
      toast.success('Atrakcja usunięta');
      loadAttractions();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas usuwania');
      console.error('Error deleting attraction:', error);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="news-management">
      <div className="page-header">
        <h2>Zarządzanie atrakcjami</h2>
        <button className="btn btn-primary" onClick={() => {
          setEditingAttraction(null);
          setFormData({
            name: '',
            description: '',
            latitude: '',
            longitude: '',
            address: '',
            status: 'draft',
          });
          setBlocks([{ 
            type: 'text', 
            text: '', 
            align: 'left',
            bold: false,
            italic: false,
            underline: false
          }]);
          setMainImageFile(null);
          setMainImagePreview(null);
          setGalleryFiles([]);
          setGalleryPreviews([]);
          setExistingGalleryImages([]);
          setSearchQuery('');
          setShowModal(true);
        }}>
          Dodaj atrakcję
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Nazwa</th>
              <th>Status</th>
              <th>Lokalizacja</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {attractions.length === 0 ? (
              <tr>
                <td colSpan="4" className="empty-state">Brak atrakcji</td>
              </tr>
            ) : (
              attractions.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    <span className={`badge ${(item.status || 'draft') === 'published' ? 'badge-success' : 'badge-warning'}`}>
                      {(item.status || 'draft') === 'published' ? 'Opublikowane' : 'Szkic'}
                    </span>
                  </td>
                  <td>{item.address || `${item.latitude}, ${item.longitude}`}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-secondary"
                        onClick={() => handlePublish(item.id, item.status || 'draft')}
                      >
                        {(item.status || 'draft') === 'published' ? 'Cofnij' : 'Opublikuj'}
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleEdit(item)}
                      >
                        Edytuj
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => {
          if (!uploadingMainImage && !uploadingGallery) {
            setShowModal(false);
            setEditingAttraction(null);
            setFormData({
              name: '',
              description: '',
              latitude: '',
              longitude: '',
              address: '',
              status: 'draft',
            });
            setBlocks([{ 
              type: 'text', 
              text: '', 
              align: 'left',
              bold: false,
              italic: false,
              underline: false
            }]);
            setMainImageFile(null);
            setMainImagePreview(null);
            setGalleryFiles([]);
            setGalleryPreviews([]);
            setExistingGalleryImages([]);
            setSearchQuery('');
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingAttraction ? 'Edytuj atrakcję' : 'Dodaj atrakcję'}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowModal(false);
                  setEditingAttraction(null);
                  setFormData({
                    name: '',
                    description: '',
                    latitude: '',
                    longitude: '',
                    address: '',
                    status: 'draft',
                  });
                  setBlocks([{ 
                    type: 'text', 
                    text: '', 
                    align: 'left',
                    bold: false,
                    italic: false,
                    underline: false
                  }]);
                  setMainImageFile(null);
                  setMainImagePreview(null);
                  setGalleryFiles([]);
                  setGalleryPreviews([]);
                  setExistingGalleryImages([]);
                  setSearchQuery('');
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Nazwa *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Opis *</label>
                <BlockEditor blocks={blocks} onBlocksChange={setBlocks} />
              </div>
              <div className="form-group">
                <label className="form-label">Lokalizacja *</label>
                <div style={{ marginBottom: '10px' }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="form-input"
                    placeholder="Wpisz miejscowość lub kod pocztowy (autouzupełnianie)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                    Wybierz z listy autouzupełniania lub kliknij na mapie, aby ustawić lokalizację.
                  </div>
                </div>
                {!isLoaded ? (
                  <div style={{
                    width: '100%',
                    height: '400px',
                    marginBottom: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                  }}>
                    Ładowanie mapy...
                  </div>
                ) : loadError ? (
                  <div style={{
                    width: '100%',
                    height: '400px',
                    marginBottom: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fee',
                    color: '#c00',
                  }}>
                    Błąd ładowania mapy
                  </div>
                ) : (
                  <div
                    ref={mapDivRef}
                    style={{
                      width: '100%',
                      height: '400px',
                      marginBottom: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                )}
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Kliknij na mapie, aby ustawić pinezkę. Możesz przeciągać pinezkę.
                </div>
                <input
                  type="hidden"
                  value={formData.latitude}
                  required
                />
                <input
                  type="hidden"
                  value={formData.longitude}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Adres</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Zdjęcie główne atrakcji *</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMainImageSelect}
                  className="form-input"
                />
                {mainImagePreview && (
                  <div className="image-preview-container">
                    <img src={mainImagePreview} alt="Preview" className="image-preview" />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => {
                        setMainImageFile(null);
                        setMainImagePreview(null);
                      }}
                    >
                      Usuń zdjęcie
                    </button>
                  </div>
                )}
                {uploadingMainImage && (
                  <div className="upload-loader">Przesyłanie zdjęcia...</div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Galeria zdjęć atrakcji</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGallerySelect}
                  className="form-input"
                />
                {uploadingGallery && (
                  <div className="upload-loader">Przesyłanie galerii...</div>
                )}
                {(galleryPreviews.length > 0 || existingGalleryImages.length > 0) && (
                  <div className="gallery-preview-container">
                    {/* Nowe zdjęcia (przed zapisem) */}
                    {galleryPreviews.map((preview, index) => (
                      <div key={`new-${index}`} className="gallery-item">
                        <img src={preview.preview} alt={`Preview ${index + 1}`} className="gallery-preview" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeGalleryFile(index)}
                        >
                          Usuń
                        </button>
                      </div>
                    ))}
                    {/* Istniejące zdjęcia (z bazy) */}
                    {existingGalleryImages.map((img) => (
                      <div key={img.id} className="gallery-item">
                        <img src={img.image_url} alt="Gallery" className="gallery-preview" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => deleteGalleryImage(img.id, img.image_url)}
                        >
                          Usuń
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    setFormData({ ...formData, status: 'draft' });
                    handleSubmit(e);
                  }}
                  disabled={uploadingMainImage || uploadingGallery}
                >
                  {(uploadingMainImage || uploadingGallery) ? 'Zapisywanie...' : 'Zapisz jako szkic'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    setFormData({ ...formData, status: 'published' });
                    handleSubmit(e);
                  }}
                  disabled={uploadingMainImage || uploadingGallery}
                >
                  {(uploadingMainImage || uploadingGallery) ? 'Zapisywanie...' : 'Opublikuj'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingAttraction(null);
                    setFormData({
                      name: '',
                      description: '',
                      latitude: '',
                      longitude: '',
                      address: '',
                      status: 'draft',
                    });
                    setBlocks([{ 
                      type: 'text', 
                      text: '', 
                      align: 'left',
                      bold: false,
                      italic: false,
                      underline: false
                    }]);
                    setMainImageFile(null);
                    setMainImagePreview(null);
                    setGalleryFiles([]);
                    setGalleryPreviews([]);
                    setExistingGalleryImages([]);
                    setSearchQuery('');
                  }}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

